import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Plus,
    Edit,
    Trash2,
    Search,
    Users,
    Phone,
    Mail,
    Percent,
    MoreVertical,
    MessageCircle,
    Loader2,
    UserCheck,
    UserX
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const emptyVendor = {
    email: '',
    password: '',
    name: '',
    phone: '',
    telegram_chat_id: '',  // Telegram Chat ID for notifications
    telegram_notifications_enabled: true,  // Toggle Telegram notifications
    commission_model: 'percentage',  // 'percentage' or 'profit_share'
    commission_rate: 50,
    pa_fee_enabled: true,  // Toggle 5% PA marketing fee
    permissions: ['view_orders', 'edit_orders', 'view_client_info'],
};

export default function Vendeurs() {
    const { user } = useAuth();
    
    // Confirmateur cannot see commission info
    const canSeeCommissions = user?.role !== 'confirmateur';
    // Confirmateur cannot create/modify vendors
    const canManageVendors = user?.role === 'admin' || user?.role === 'super_admin';
    
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [formData, setFormData] = useState(emptyVendor);
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    
    // Fetch vendors
    const fetchVendors = useCallback(async () => {
        setLoading(true);
        try {
            // Use /vendeurs endpoint which returns vendors directly
            const response = await api.get('/vendeurs');
            const vendorsList = response.data || [];
            setVendors(vendorsList);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toast.error('Erreur lors du chargement des vendeurs');
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);
    
    // Filter vendors by search
    const filteredVendors = vendors.filter(vendor => 
        vendor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.phone?.includes(searchQuery)
    );
    
    // Open dialog for create/edit
    const handleOpenDialog = (vendor = null) => {
        if (vendor) {
            setEditingVendor(vendor);
            setFormData({
                email: vendor.email || '',
                password: '', // Don't pre-fill password
                name: vendor.name || '',
                phone: vendor.phone || '',
                telegram_chat_id: vendor.telegram_chat_id || '',
                telegram_notifications_enabled: vendor.telegram_notifications_enabled !== false,  // Default to true
                commission_model: vendor.commission_model || 'percentage',
                commission_rate: vendor.commission_rate || 50,
                pa_fee_enabled: vendor.pa_fee_enabled !== false,  // Default to true
                permissions: vendor.permissions || ['view_orders', 'edit_orders', 'view_client_info'],
            });
        } else {
            setEditingVendor(null);
            setFormData(emptyVendor);
        }
        setDialogOpen(true);
    };
    
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingVendor(null);
        setFormData(emptyVendor);
    };
    
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };
    
    // Submit vendor
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email) {
            toast.error('Nom et email sont requis');
            return;
        }
        
        if (!editingVendor && !formData.password) {
            toast.error('Mot de passe requis pour un nouveau vendeur');
            return;
        }
        
        setSubmitting(true);
        try {
            const vendorData = {
                name: formData.name,
                email: formData.email,
                role: 'vendeur',
                phone: formData.phone,
                commission_model: formData.commission_model || 'percentage',
                commission_rate: parseFloat(formData.commission_rate) || 50,
                pa_fee_enabled: formData.pa_fee_enabled,
                permissions: formData.permissions || ['view_orders', 'edit_orders', 'view_client_info'],
                telegram_notifications_enabled: formData.telegram_notifications_enabled,
            };
            
            // Only include non-empty optional fields (preserve existing values)
            if (formData.telegram_chat_id) {
                vendorData.telegram_chat_id = formData.telegram_chat_id;
            }
            
            // Don't send empty password on edit
            if (formData.password) {
                vendorData.password = formData.password;
            } else if (!editingVendor) {
                // Password required for new vendor
                toast.error('Mot de passe requis pour un nouveau vendeur');
                setSubmitting(false);
                return;
            }
            
            if (editingVendor) {
                await api.put(`/users/${editingVendor.id}`, vendorData);
                toast.success('Vendeur mis à jour');
            } else {
                await api.post('/users', vendorData);
                toast.success('Vendeur créé');
            }
            
            handleCloseDialog();
            fetchVendors();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };
    
    // Toggle vendor active status
    const toggleVendorStatus = async (vendor) => {
        try {
            await api.put(`/users/${vendor.id}`, {
                is_active: !vendor.is_active
            });
            toast.success(vendor.is_active ? 'Vendeur désactivé' : 'Vendeur activé');
            fetchVendors();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };
    
    // Delete vendor
    const handleDelete = async () => {
        if (!vendorToDelete) return;
        
        try {
            await api.delete(`/users/${vendorToDelete.id}`);
            toast.success('Vendeur supprimé');
            setDeleteDialogOpen(false);
            setVendorToDelete(null);
            fetchVendors();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };
    
    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">Vendeurs</h1>
                        <p className="text-muted-foreground">
                            Gérez vos vendeurs et leurs commissions
                        </p>
                    </div>
                    {canManageVendors && (
                    <Button onClick={() => handleOpenDialog()}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau Vendeur
                    </Button>
                    )}
                </div>
                
                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher un vendeur..."
                        className="pl-10"
                    />
                </div>
                
                {/* Vendors Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                ) : filteredVendors.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                            <h3 className="text-lg font-medium">Aucun vendeur</h3>
                            <p className="text-muted-foreground mt-1">
                                {canManageVendors ? "Créez votre premier vendeur pour commencer" : "Aucun vendeur disponible"}
                            </p>
                            {canManageVendors && (
                            <Button className="mt-4" onClick={() => handleOpenDialog()}>
                                <Plus className="w-4 h-4 mr-2" />
                                Nouveau Vendeur
                            </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredVendors.map((vendor) => (
                            <Card key={vendor.id} className={cn(!vendor.is_active && "opacity-60")}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium",
                                                vendor.is_active ? "bg-primary" : "bg-muted-foreground"
                                            )}>
                                                {vendor.name?.charAt(0).toUpperCase() || 'V'}
                                            </div>
                                            <div>
                                                <CardTitle className="text-base">{vendor.name}</CardTitle>
                                                <Badge variant={vendor.is_active ? "default" : "secondary"} className="mt-1">
                                                    {vendor.is_active ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </div>
                                        </div>
                                        {canManageVendors && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleOpenDialog(vendor)}>
                                                    <Edit className="w-4 h-4 mr-2" />
                                                    Modifier
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => toggleVendorStatus(vendor)}>
                                                    {vendor.is_active ? (
                                                        <>
                                                            <UserX className="w-4 h-4 mr-2" />
                                                            Désactiver
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UserCheck className="w-4 h-4 mr-2" />
                                                            Activer
                                                        </>
                                                    )}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    className="text-destructive"
                                                    onClick={() => {
                                                        setVendorToDelete(vendor);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Supprimer
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <span className="truncate">{vendor.email}</span>
                                    </div>
                                    {vendor.phone && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <span>{vendor.phone}</span>
                                        </div>
                                    )}
                                    {vendor.telegram_chat_id && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-blue-500">📱</span>
                                            <span className="text-blue-600">{vendor.telegram_chat_id}</span>
                                            {vendor.telegram_notifications_enabled !== false ? (
                                                <Badge className="bg-blue-100 text-blue-700 text-xs px-1.5">🔔 ON</Badge>
                                            ) : (
                                                <Badge className="bg-red-100 text-red-700 text-xs px-1.5">🔕 OFF</Badge>
                                            )}
                                        </div>
                                    )}
                                    {canSeeCommissions && (
                                    <div className="flex items-center gap-2 text-sm pt-2 border-t">
                                        <Percent className="w-4 h-4 text-blue-500" />
                                        <div className="flex flex-col">
                                            <span className="font-medium">
                                                {vendor.commission_model === 'profit_share' 
                                                    ? '💎 Partage de Profit (5% + 50/50)' 
                                                    : `📊 Commission: ${vendor.commission_rate || 50}%`}
                                            </span>
                                            {vendor.commission_model === 'profit_share' && (
                                                <span className="text-xs text-muted-foreground">
                                                    PA 5% marketing + Profit 50/50
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingVendor ? 'Modifier le vendeur' : 'Nouveau vendeur'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nom complet *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                placeholder="Nom du vendeur"
                                required
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleFormChange('email', e.target.value)}
                                placeholder="email@exemple.com"
                                required
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>{editingVendor ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</Label>
                            <Input
                                type="password"
                                value={formData.password}
                                onChange={(e) => handleFormChange('password', e.target.value)}
                                placeholder="••••••••"
                                required={!editingVendor}
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Téléphone</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => handleFormChange('phone', e.target.value)}
                                    placeholder="0555123456"
                                />
                            </div>
                        </div>
                        
                        {/* Telegram Section */}
                        <div className="space-y-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
                            <Label className="text-blue-900 font-semibold">📱 Telegram</Label>
                            
                            <div className="space-y-2">
                                <Label className="text-sm">Chat ID</Label>
                                <Input
                                    value={formData.telegram_chat_id || ''}
                                    onChange={(e) => handleFormChange('telegram_chat_id', e.target.value)}
                                    placeholder="123456789"
                                />
                                <p className="text-xs text-blue-600">
                                    💡 Le vendeur doit envoyer /start à @NouvelleCommandeBot pour obtenir son Chat ID
                                </p>
                            </div>
                            
                            <div className="flex items-center justify-between pt-2">
                                <div>
                                    <Label className="text-blue-800">Notifications Telegram</Label>
                                    <p className="text-xs text-blue-600">Recevoir les alertes de nouvelles commandes</p>
                                </div>
                                <Switch
                                    checked={formData.telegram_notifications_enabled !== false}
                                    onCheckedChange={(checked) => handleFormChange('telegram_notifications_enabled', checked)}
                                />
                            </div>
                        </div>
                        
                        {canSeeCommissions && (
                        <div className="space-y-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                            <Label className="text-amber-900 font-semibold">💰 Modèle de Commission</Label>
                            
                            <div className="space-y-2">
                                <Select
                                    value={formData.commission_model || 'percentage'}
                                    onValueChange={(value) => handleFormChange('commission_model', value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percentage">
                                            📊 Commission Fixe (%)
                                        </SelectItem>
                                        <SelectItem value="profit_share">
                                            💎 Partage de Profit (PA 5% + 50/50)
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {formData.commission_model === 'percentage' && (
                                <div className="space-y-2">
                                    <Label>Taux de Commission (%)</Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={formData.commission_rate}
                                            onChange={(e) => handleFormChange('commission_rate', e.target.value)}
                                            className="w-24"
                                        />
                                        <span className="text-muted-foreground">%</span>
                                    </div>
                                    <p className="text-xs text-amber-700">
                                        Ex: 50% = Le vendeur reçoit 50% du profit net
                                    </p>
                                </div>
                            )}
                            
                            {formData.commission_model === 'profit_share' && (
                                <div className="space-y-2 p-3 bg-white rounded border border-amber-300">
                                    <p className="text-sm font-medium text-amber-900">Calcul automatique :</p>
                                    <ul className="text-xs text-amber-700 space-y-1">
                                        <li>• PA prend 5% du prix de vente (marketing) {!formData.pa_fee_enabled && <span className="text-red-600 font-bold">- DÉSACTIVÉ</span>}</li>
                                        <li>• Profit net = Client paie - Prix achat - Livraison - Frais {formData.pa_fee_enabled && '- PA 5%'}</li>
                                        <li>• Profit partagé 50/50</li>
                                        <li>• Vendeur récupère: Prix achat + Frais + 50% profit</li>
                                    </ul>
                                </div>
                            )}
                            
                            {/* PA 5% Fee Toggle */}
                            <div className="flex items-center justify-between p-3 bg-purple-50 rounded border border-purple-200">
                                <div>
                                    <Label className="text-purple-900 font-medium">💰 Frais PA 5%</Label>
                                    <p className="text-xs text-purple-700">
                                        {formData.pa_fee_enabled 
                                            ? "5% prélevé sur le prix de vente (marketing)" 
                                            : "Pas de frais 5% pour ce vendeur"
                                        }
                                    </p>
                                </div>
                                <Switch
                                    checked={formData.pa_fee_enabled !== false}
                                    onCheckedChange={(checked) => handleFormChange('pa_fee_enabled', checked)}
                                />
                            </div>
                        </div>
                        )}
                        
                        <div className="space-y-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
                            <Label className="text-blue-900 font-semibold">🔐 Permissions</Label>
                            <div className="space-y-2">
                                {[
                                    { id: 'view_orders', label: 'Voir les commandes' },
                                    { id: 'edit_orders', label: 'Modifier les commandes' },
                                    { id: 'view_client_info', label: 'Voir infos clients (après claim)' },
                                    { id: 'view_reports', label: 'Voir les rapports' },
                                ].map((perm) => (
                                    <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.permissions?.includes(perm.id)}
                                            onChange={(e) => {
                                                const newPerms = e.target.checked
                                                    ? [...(formData.permissions || []), perm.id]
                                                    : formData.permissions.filter(p => p !== perm.id);
                                                handleFormChange('permissions', newPerms);
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm">{perm.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleCloseDialog}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {editingVendor ? 'Enregistrer' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer le vendeur</DialogTitle>
                    </DialogHeader>
                    <p>
                        Êtes-vous sûr de vouloir supprimer <strong>{vendorToDelete?.name}</strong> ?
                        Cette action est irréversible.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
